import { useState, useEffect, useRef } from "react";
import type { Input } from "../../services/inputsApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input as InputComponent } from "@/components/ui/input";
import { MoreHorizontal, Search, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteInput } from "../../hooks/useInputs";
import { EditInputsDialog } from "./EditInputDialog";
import { BatchesDialog } from "../batches/BatchesDialog";
import { useInputTypes } from "../../hooks/useInputTypes";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";

interface InputsTableProps {
  inputs: Input[];
  search?: string;
  onSearchChange?: (value: string) => void;
}

export const InputTable = ({ inputs, search = '', onSearchChange }: InputsTableProps) => {
  const [localSearch, setLocalSearch] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local value if parent resets search (e.g. on page change)
  useEffect(() => {
    if (search === '') setLocalSearch('');
  }, [search]);

  const handleSearchInput = (value: string) => {
    setLocalSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 300);
  };

  const deleteMutation = useDeleteInput();
  const confirm = useConfirm();
  const { data: inputTypes = [] } = useInputTypes();

  const inputTypeMap = new Map(
    inputTypes.map((type) => [type.id, type.name])
  );

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: "Delete input?",
      description: `“${name}” will be permanently removed. This can't be undone.`,
      variant: "destructive",
      confirmLabel: "Delete",
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} deleted`),
    });
  };

  const formatCurrency = (value?: number | null) => {
    if (value === null || value === undefined) {
      return "-";
    }

    return `KES ${value.toLocaleString()}`;
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <InputComponent
          placeholder="Search inputs..."
          value={localSearch}
          onChange={(e) => handleSearchInput(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Input Name</TableHead>
              <TableHead>Variety</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Current Stock</TableHead>
              <TableHead>Unit Cost</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {inputs.map((input) => (
              <TableRow key={input.id}>
                <TableCell className="font-medium">{input.inputName}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {input.variety || <span className="text-gray-400">—</span>}
                </TableCell>
                <TableCell className="text-sm">{input.inputCode}</TableCell>
                <TableCell className="text-sm">
                  <Badge variant="outline">
                    {inputTypeMap.get(input.inputTypeId) || "Unknown"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{input.unitOfMeasureDisplay || input.unitOfMeasure}</TableCell>
                <TableCell className="text-sm">
                  {input.currentStock ?? 0}
                </TableCell>
                <TableCell className="text-sm">
                  {formatCurrency(input.unitCost)}
                </TableCell>
                <TableCell className="text-sm">
                  {input.hasExpiryDate ? (
                    <Badge variant="outline">{input.shelfLifeDays} days</Badge>
                  ) : (
                    <span className="text-gray-500">No expiry</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={input.isActive ? "default" : "destructive"}>
                    {input.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <EditInputsDialog input={input} />
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <BatchesDialog input={input} />
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(input.id, input.inputName)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {inputs.length === 0 && (
        <div className="text-center py-8 text-gray-500">No inputs found</div>
      )}
    </div>
  );
};
